import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { PortfolioBacktest, TradingProfile } from '@einhaus/project314-types';
// Imported directly rather than via the barrel: the barrel pulls in trpc-backed selects, which
// cannot load in the DOM-less component tests that render this file.
import Tooltip from '@components/UI/Tooltip';
import { describePositionSizeRule } from '@/utils/capitalRuleFormatters';
import {
    buildFrozenCapitalChips,
    buildFrozenSelectionChips,
    describeFrozenStrategySizing,
    type FrozenSnapshotDrift
} from './portfolioBacktestFrozenSnapshot';
import { backtestUsesCohortBestBacktestSelection, BEST_BACKTEST_EVIDENCE_MODE_DETAILS } from './portfolioBacktestEvidenceMode';

/** Number of strategies named in the collapsed cell before it falls back to a count. */
const MAX_CELL_STRATEGIES = 2;

const DRIFT_LABEL = 'Differs from live';

type ChipTone = 'neutral' | 'warning';

const chipToneClasses: Record<ChipTone, string> = {
    neutral: 'bg-zinc-800 text-zinc-300',
    warning: 'bg-amber-950 text-amber-300'
};

/**
 * Badge with an instant styled tooltip: the first `title` line reads as a dimmed headline, the
 * remaining lines as the itemised detail. The native `title` attribute is deliberately not used —
 * its hover delay and unstyled rendering made the detail too easy to miss.
 */
const Chip = ({ children, title, tone = 'neutral' }: { children: ReactNode; title?: string; tone?: ChipTone }) => {
    const chip = (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs whitespace-nowrap ${chipToneClasses[tone]}`}>
            {children}
        </span>
    );

    if (!title) return chip;

    const [headline, ...detailLines] = title.split('\n');

    return (
        <Tooltip
            content={
                detailLines.length > 0 ? (
                    <div className="space-y-1">
                        <div className="text-zinc-400">{headline}</div>
                        <div className="space-y-0.5">
                            {detailLines.map((line, lineIndex) => (
                                // Lines are display-only and stable per render, so the index is a safe key.
                                <div key={lineIndex}>{line}</div>
                            ))}
                        </div>
                    </div>
                ) : (
                    title
                )
            }
        >
            {chip}
        </Tooltip>
    );
};

const strategyEditorPath = (strategyId: number): string => `/user/tradingStrategy/${strategyId.toString()}`;

/** Name plus id, so an unnamed strategy reads as `Strategy #297` instead of `Strategy 297 #297`. */
const strategyDisplayName = (strategyId: number, strategyNameMap: ReadonlyMap<number, string>): string => {
    const resolvedName = strategyNameMap.get(strategyId);

    return resolvedName ? `${resolvedName} (#${strategyId.toString()})` : `Strategy #${strategyId.toString()}`;
};

interface StrategyIdentityProps {
    strategyId: number;
    strategyNameMap: ReadonlyMap<number, string>;
}

/**
 * A strategy's identity in the collapsed cell: its name linked to the strategy editor, with the id
 * held in a sibling element rather than inside the clamped name, so the id survives truncation of
 * the long prefix-family names these runs use.
 */
const StrategyIdentity = ({ strategyId, strategyNameMap }: StrategyIdentityProps) => {
    const resolvedName = strategyNameMap.get(strategyId);

    return (
        <div className="flex min-w-0 items-start gap-1.5">
            <Link
                className="line-clamp-2 min-w-0 flex-1 text-xs leading-5 text-primary hover:text-blue-400"
                title={`Open ${strategyDisplayName(strategyId, strategyNameMap)}`}
                to={strategyEditorPath(strategyId)}
            >
                {resolvedName ?? `Strategy #${strategyId.toString()}`}
            </Link>
            {/* Redundant when the name has not loaded, because the link already reads `Strategy #297`. */}
            {resolvedName !== undefined && <span className="shrink-0 font-mono text-[11px] leading-5 text-zinc-500">#{strategyId}</span>}
        </div>
    );
};

/**
 * A frozen OBS or exit candidate in the expanded row: resolved name linked to its editor with the id
 * as a copyable sibling, falling back to the bare id link while the name is unknown.
 */
const FrozenReferenceLink = ({ id, name, path }: { id: number; name: string | undefined; path: string }) => (
    <span className="inline-flex max-w-full items-baseline gap-1">
        <Link
            className="max-w-[260px] truncate text-blue-300 hover:text-blue-200"
            title={name === undefined ? undefined : `${name} (#${id.toString()})`}
            to={path}
        >
            {name ?? `#${id.toString()}`}
        </Link>
        {name !== undefined && <span className="shrink-0 font-mono text-zinc-500">#{id}</span>}
    </span>
);

interface FrozenStrategiesCellProps {
    backtest: PortfolioBacktest;
    drift: FrozenSnapshotDrift;
    exitCandidateLabelMap: ReadonlyMap<number, string>;
    optionBuyingStrategyNameMap: ReadonlyMap<number, string>;
    strategyNameMap: ReadonlyMap<number, string>;
}

/**
 * Collapsed view of a run's strategies: names plus a single line of chips summarising what the run
 * froze. The itemised snapshot, trading profiles included, lives in the expandable row so the table
 * stays scannable; the chip tooltips itemise the frozen selections by name for anyone who hovers
 * instead of expanding.
 */
export const FrozenStrategiesCell = ({
    backtest,
    drift,
    exitCandidateLabelMap,
    optionBuyingStrategyNameMap,
    strategyNameMap
}: FrozenStrategiesCellProps) => {
    const strategies = backtest.strategiesSubscribed;
    const visibleStrategies = strategies.slice(0, MAX_CELL_STRATEGIES);
    const hiddenStrategies = strategies.slice(MAX_CELL_STRATEGIES);
    const selectionChips = buildFrozenSelectionChips(backtest, { exitCandidateLabelMap, optionBuyingStrategyNameMap, strategyNameMap });
    const hasChipRow = selectionChips.length > 0 || drift.status === 'differsFromLive';

    return (
        <div className="flex min-w-0 flex-col gap-1">
            {strategies.length === 0 ? (
                <div className="text-xs text-zinc-500">No strategies recorded</div>
            ) : (
                visibleStrategies.map((strategy) => (
                    <StrategyIdentity key={strategy.id} strategyId={strategy.id} strategyNameMap={strategyNameMap} />
                ))
            )}

            {hiddenStrategies.length > 0 && (
                <div
                    className="text-xs text-zinc-500"
                    title={hiddenStrategies.map((strategy) => strategyDisplayName(strategy.id, strategyNameMap)).join('\n')}
                >
                    +{hiddenStrategies.length.toLocaleString()} more — expand the row to open them
                </div>
            )}

            {hasChipRow && (
                <div className="flex flex-wrap items-center gap-1">
                    {selectionChips.map((chip) => (
                        <Chip key={chip.label} title={chip.title}>
                            {chip.label}
                        </Chip>
                    ))}
                    {drift.status === 'differsFromLive' && (
                        <Chip title={`Differs from the live deployment: ${drift.differences.join(', ')}`} tone="warning">
                            {DRIFT_LABEL}
                        </Chip>
                    )}
                </div>
            )}
        </div>
    );
};

interface FrozenRunConfigurationProps {
    backtest: PortfolioBacktest;
    drift: FrozenSnapshotDrift;
    exitCandidateLabelMap: ReadonlyMap<number, string>;
    optionBuyingStrategyNameMap: ReadonlyMap<number, string>;
    strategyNameMap: ReadonlyMap<number, string>;
    onViewTradingProfile: (tradingProfile: TradingProfile) => void;
}

/**
 * Expanded-row detail panel holding the run's full frozen snapshot. Laid out horizontally so the
 * per-strategy settings of one run can be read (and compared against another run) at a glance.
 */
export const FrozenRunConfiguration = ({
    backtest,
    drift,
    exitCandidateLabelMap,
    optionBuyingStrategyNameMap,
    strategyNameMap,
    onViewTradingProfile
}: FrozenRunConfigurationProps) => {
    const capitalChips = buildFrozenCapitalChips(backtest);
    const defaultRulesWereRecorded = backtest.settings.positionSizeRules !== undefined;
    const usesCohortSelection = backtestUsesCohortBestBacktestSelection(backtest.strategiesSubscribed);
    const evidenceModeDetails = BEST_BACKTEST_EVIDENCE_MODE_DETAILS[backtest.backtestSelectionArtifactUniverseMode ?? 'strict_operational'];

    return (
        <div className="space-y-3 p-4">
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium tracking-wide text-zinc-400 uppercase">Frozen run configuration</span>
                {drift.status === 'differsFromLive' && <Chip tone="warning">{DRIFT_LABEL}</Chip>}
            </div>

            <p className="text-xs text-zinc-500">
                These rules are the immutable snapshot this run used. Current portfolio settings may be different.
            </p>

            {usesCohortSelection && (
                <div
                    className={`rounded border px-3 py-2 text-xs ${
                        evidenceModeDetails.isResearchOnly
                            ? 'border-amber-900/60 bg-amber-950/30 text-amber-200'
                            : 'border-blue-900/60 bg-blue-950/30 text-blue-200'
                    }`}
                >
                    <div className="font-medium">Cohort selection evidence: {evidenceModeDetails.resultLabel}</div>
                    <p className="mt-1 opacity-80">{evidenceModeDetails.description}</p>
                </div>
            )}

            <div className="flex flex-wrap gap-2">
                {capitalChips.map((chip) => (
                    <div className="rounded border border-zinc-700 bg-zinc-950/40 px-3 py-1.5 text-xs" key={chip.label}>
                        <span className="text-zinc-500">{chip.label}: </span>
                        <span className={chip.isRecorded ? 'text-zinc-200' : 'text-amber-300'}>{chip.value}</span>
                    </div>
                ))}
            </div>

            {drift.differences.length > 0 && (
                <div className="rounded border border-amber-900/60 bg-amber-950/30 px-3 py-2 text-xs text-amber-200">
                    <div className="font-medium">Differs from the portfolio&apos;s live deployment</div>
                    <ul className="mt-1 list-inside list-disc space-y-0.5 text-amber-300/90">
                        {drift.differences.map((difference) => (
                            <li key={difference}>{difference}</li>
                        ))}
                    </ul>
                </div>
            )}

            {backtest.strategiesSubscribed.length === 0 ? (
                <div className="text-sm text-zinc-400">No strategies were recorded for this run.</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px] text-left text-xs">
                        <thead className="text-zinc-500">
                            <tr>
                                <th className="py-1 pr-4 font-normal">Strategy</th>
                                <th className="py-1 pr-4 font-normal">Trading profile</th>
                                <th className="py-1 pr-4 font-normal">Frozen OBS</th>
                                <th className="py-1 pr-4 font-normal">Frozen exits</th>
                                <th className="py-1 font-normal">Position sizing</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-800">
                            {backtest.strategiesSubscribed.map((strategy) => {
                                const { tradingProfile } = strategy;
                                const sizing = describeFrozenStrategySizing(strategy, defaultRulesWereRecorded);
                                const strategyRules = strategy.portfolioBacktestPositionSizeRules;

                                return (
                                    <tr key={strategy.id}>
                                        <td className="py-2 pr-4 align-top">
                                            <Link
                                                className="text-primary hover:text-blue-400"
                                                title={`Open ${strategyDisplayName(strategy.id, strategyNameMap)}`}
                                                to={strategyEditorPath(strategy.id)}
                                            >
                                                {strategyNameMap.get(strategy.id) ?? `Strategy #${strategy.id.toString()}`}
                                            </Link>{' '}
                                            {strategyNameMap.has(strategy.id) && (
                                                <span className="font-mono text-zinc-500">#{strategy.id}</span>
                                            )}
                                        </td>
                                        <td className="py-2 pr-4 align-top">
                                            {tradingProfile ? (
                                                <button
                                                    className="text-blue-300 hover:text-blue-200"
                                                    type="button"
                                                    onClick={() => onViewTradingProfile(tradingProfile)}
                                                >
                                                    {tradingProfile.name}
                                                </button>
                                            ) : (
                                                <span className="text-zinc-500">Not recorded</span>
                                            )}
                                        </td>
                                        <td className="py-2 pr-4 align-top">
                                            {strategy.optionBuyingStrategyIds?.length ? (
                                                <span className="flex flex-wrap gap-x-3 gap-y-0.5">
                                                    {strategy.optionBuyingStrategyIds.map((optionBuyingStrategyId) => (
                                                        <FrozenReferenceLink
                                                            id={optionBuyingStrategyId}
                                                            key={optionBuyingStrategyId}
                                                            name={optionBuyingStrategyNameMap.get(optionBuyingStrategyId)}
                                                            path={`/user/optionBuyingStrategy/${optionBuyingStrategyId.toString()}`}
                                                        />
                                                    ))}
                                                </span>
                                            ) : (
                                                <span className="text-zinc-600">—</span>
                                            )}
                                        </td>
                                        <td className="py-2 pr-4 align-top">
                                            {strategy.exitCandidateIds?.length ? (
                                                <span className="flex flex-wrap gap-x-3 gap-y-0.5">
                                                    {strategy.exitCandidateIds.map((exitCandidateId) => (
                                                        <FrozenReferenceLink
                                                            id={exitCandidateId}
                                                            key={exitCandidateId}
                                                            name={exitCandidateLabelMap.get(exitCandidateId)}
                                                            path={`/user/exitCandidate/${exitCandidateId.toString()}`}
                                                        />
                                                    ))}
                                                </span>
                                            ) : (
                                                <span className="text-zinc-600">—</span>
                                            )}
                                        </td>
                                        <td className="py-2 align-top">
                                            {sizing.kind === 'override' && strategyRules ? (
                                                <ul className="space-y-0.5 text-zinc-200">
                                                    {strategyRules.map((rule) => (
                                                        <li key={rule.id}>{describePositionSizeRule(rule)}</li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <span className={sizing.kind === 'notRecorded' ? 'text-amber-300' : 'text-zinc-400'}>
                                                    {sizing.text}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
